/**
 * ISO 27001 AUDIT METADATA STAMPING
 * Shared utility to ensure all exports (JSON/CSV) contain the required audit block.
 */

export interface ExportAuditMetadata {
    exporterUid: string;
    organization: string;
    timestamp: string;
}

/**
 * Generates the audit metadata header for CSV files.
 * Format: # ISO 27001 AUDIT METADATA
 *         # Organization, <Org>
 *         # Exporter UID, <UID>
 *         # Export Timestamp, <Timestamp>
 */
export function generateCSVAuditHeader(metadata: ExportAuditMetadata): string {
    return [
        '# ISO 27001 AUDIT METADATA',
        `# Organization,${metadata.organization}`,
        `# Exporter UID,${metadata.exporterUid}`,
        `# Export Timestamp,${metadata.timestamp}`,
        '', // Spacer line
        ''
    ].join('\n');
}

/**
 * Wraps data with audit metadata for JSON exports.
 */
export function wrapWithAuditMetadata(data: any, metadata: ExportAuditMetadata) {
    return {
        _audit_metadata: {
            exporter_uid: metadata.exporterUid,
            organization: metadata.organization,
            export_timestamp: metadata.timestamp,
        },
        payload: data
    };
}

/**
 * Helper to trigger a browser download of a CSV file with mandatory audit stamping.
 */
export function downloadCSVWithAudit(
    filename: string,
    headers: string,
    rows: string[],
    metadata: ExportAuditMetadata
) {
    const auditHeader = generateCSVAuditHeader(metadata);
    const csvContent = auditHeader + headers + '\n' + rows.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/**
 * Helper to trigger a browser download of a JSON file with mandatory audit stamping.
 */
export function downloadJSONWithAudit(
    filename: string,
    data: any,
    metadata: ExportAuditMetadata
) {
    const exportData = wrapWithAuditMetadata(data, metadata);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
